import { Component, lazy, Suspense, useEffect, useState } from "react";
import { Routes, Route, Outlet, useLocation } from "react-router-dom";
import { applySeo, getSeo } from "@/seo";
import { trackPageView } from "@/lib/pixel";
import SiteLayout from "@/components/SiteLayout";
import Landing from "@/pages/Landing";

const NotFound = lazy(() => import("@/pages/NotFound"));
const Toaster = lazy(() => import("@/components/ui/sonner").then((m) => ({ default: m.Toaster })));
const Work = lazy(() => import("@/pages/Work"));
const WorkDetail = lazy(() => import("@/pages/WorkDetail"));
const ServicesPage = lazy(() => import("@/pages/ServicesPage"));
const ServiceDetail = lazy(() => import("@/pages/ServiceDetail"));
const About = lazy(() => import("@/pages/About"));
const Security = lazy(() => import("@/pages/Security"));
const Contact = lazy(() => import("@/pages/Contact"));

const Admin = lazy(() => import("@/pages/Admin"));
const Pay = lazy(() => import("@/pages/Pay"));
const PaymentSuccess = lazy(() => import("@/pages/PaymentResult").then((m) => ({ default: m.PaymentSuccess })));
const PaymentCancel = lazy(() => import("@/pages/PaymentResult").then((m) => ({ default: m.PaymentCancel })));

const Fallback = () => <div className="min-h-screen bg-void" />;

class ChunkErrorBoundary extends Component {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch(error) {
    const stale = /Loading chunk|Loading CSS chunk|dynamically imported module|Failed to fetch|Unexpected token '<'/i.test(String(error?.message));
    if (stale && !sessionStorage.getItem("chunk-reload")) {
      sessionStorage.setItem("chunk-reload", "1");
      window.location.reload();
    }
  }
  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <div className="min-h-screen bg-void flex items-center justify-center px-6 text-center">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-magenta mb-4">{"// A newer version is available"}</p>
          <button onClick={() => { sessionStorage.removeItem("chunk-reload"); window.location.reload(); }} className="rounded-full bg-cyan-400 text-[#030308] font-semibold px-6 py-3 hover:bg-mint transition-colors duration-300">Reload page</button>
        </div>
      </div>
    );
  }
}

function SeoSync() {
  const { pathname } = useLocation();
  useEffect(() => {
    applySeo(getSeo(pathname));
    trackPageView(pathname);
  }, [pathname]);
  return null;
}

// Toast host mounts client-side after first paint (not part of the prerendered HTML or the main bundle).
function DeferredToaster() {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setReady(true), 1500);
    return () => clearTimeout(t);
  }, []);
  if (!ready) return null;
  return (
    <Suspense fallback={null}>
      <Toaster position="top-right" theme="dark" />
    </Suspense>
  );
}

export function AppRoutes() {
  return (
    <div className="App">
      <SeoSync />
      <ChunkErrorBoundary>
      <Suspense fallback={<Fallback />}>
        <Routes>
          <Route element={<SiteLayout><Outlet /></SiteLayout>}>
            <Route path="/" element={<Landing />} />
            <Route path="/work" element={<Work />} />
            <Route path="/work/:slug" element={<WorkDetail />} />
            <Route path="/services" element={<ServicesPage />} />
            <Route path="/services/:slug" element={<ServiceDetail />} />
            <Route path="/about" element={<About />} />
            <Route path="/security" element={<Security />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="*" element={<NotFound />} />
          </Route>
          <Route path="/admin" element={<Admin />} />
          <Route path="/pay/:requestId" element={<Pay />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />
        </Routes>
      </Suspense>
      </ChunkErrorBoundary>
      <DeferredToaster />
    </div>
  );
}
